import React, { useEffect, useState } from "react";
import { enquiryDelete } from "../../../api/Enquiry";
import { LoadingDiv, SweetAlertShow } from "../../master/Master";
import { Link, useNavigate } from "react-router-dom";
import { formatToDate } from "../../master/FormatToDateTime";
import usePrivateLink from "../../config/usePrivateLink";
import Swal from "sweetalert2";
import Pagination from "../../components/Pagination";
import FollowUpModal from "./FollowUpModal";
import StatusModal from "./StatusModal";
import { EnquiryStatusBadge, getEnquiries } from "./Master";

export default function EnquiryTable({ filters, setFilters }) {
  const hasAccess = usePrivateLink();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [modalData, setModalData] = useState({});

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      const response = await enquiryDelete(id);
      if (response?.data?.status) {
        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.response?.message,
      );
    } finally {
      setLoading(false);
      handleFunction();
    }
  };

  const handleFunction = () => {
    getEnquiries({
      setLoading,
      filters,
      setData,
    });
  };

  useEffect(() => {
    handleFunction();
  }, [filters]);

  return (
    <>
      {loading && <LoadingDiv />}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
        <div className="d-flex align-items-center gap-2">
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              background: "#eef4ff",
            }}
          >
            <i className="ri-group-line text-primary"></i>
          </div>

          <div>
            <small className="text-muted">Total Enquiries</small>
            <h6 className="m-0 fw-bold">{data?.total || 0}</h6>
          </div>
        </div>

        <div style={{ width: "320px", maxWidth: "100%" }}>
          <div className="form-floating form-floating-outline">
            <input
              type="text"
              className="form-control"
              placeholder="Search by Name, Mobile"
              value={filters?.search || ""}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  search: e.target.value.toUpperCase(),
                })
              }
            />
            <label>
              <i className="ri-search-line me-1"></i>
              Search
            </label>
          </div>
        </div>
      </div>
      <div className="table-responsive">
        <table className="table table-hover align-middle">
          <thead
            className="text-nowrap"
            style={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              background: "#fff",
            }}
          >
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Father's Name</th>
              <th>Mobile</th>
              <th>Address</th>
              <th>Course</th>
              <th>Status</th>
              <th>Enquiry Date</th>
              <th>Next Follow Up</th>
              <th>Adding By</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody className="text-capitalize">
            {data?.data?.map((item, i) => (
              <tr
                key={item.id}
                style={{ cursor: "pointer" }}
                onClick={() => navigate(`/enquiry/detail?id=${item.id}`)}
              >
                <td>{i + 1}</td>
                <td
                  className="fw-semibold text-primary"
                  style={{ minWidth: "180px" }}
                >
                  {item.student_name}{" "}
                  {item?.enquiry_no && `(${item?.enquiry_no})`}
                </td>
                <td>{item.father_name}</td>
                <td>{item.mobile}</td>
                <td
                  style={{
                    maxWidth: "220px",
                  }}
                >
                  {item.village} {item.district} {item.state} {item.pincode}
                </td>
                <td>{item?.course?.title}</td>
                <td
                  onClick={(e) => {
                    e.stopPropagation();
                    setModalData(item);
                  }}
                >
                  {EnquiryStatusBadge(item.status)}
                </td>
                <td>{formatToDate(item?.enquiry_date)}</td>
                <td
                  onClick={(e) => {
                    e.stopPropagation();
                    setModalData(item);
                  }}
                  className="text-nowrap"
                >
                  {formatToDate(item?.next_followup_date)}{" "}
                  {hasAccess("/enquiry/followups") && (
                    <i
                      className="ri-phone-fill text-warning cursor-pointer ri-20px ms-1"
                      data-bs-toggle="modal"
                      data-bs-target="#AddNewFollowup"
                    ></i>
                  )}
                </td>
                <td>{item?.creator_by?.name || "N/A"}</td>
                <td
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {hasAccess("/enquiry/update") && (
                    <Link to={`/enquiry/update?id=${item.id}`}>
                      <i className="ri-edit-line text-info text-info cursor-pointer"></i>
                    </Link>
                  )}

                  {hasAccess("/enquiry/delete") && (
                    <i
                      className="ri-delete-bin-6-line text-danger cursor-pointer"
                      onClick={() => handleDelete(item?.id)}
                    ></i>
                  )}
                </td>
              </tr>
            ))}
            {data?.data?.length === 0 && (
              <tr>
                <td colSpan="11" className="text-center py-5">
                  <i className="ri-search-eye-line fs-1 text-muted"></i>
                  <div className="mt-2 text-muted">No enquiries found</div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <Pagination data={data} setFilters={setFilters} filters={filters} />

        <FollowUpModal
          data={modalData}
          setLoading={setLoading}
          handleFunction={handleFunction}
        />
        <StatusModal
          data={modalData}
          setData={setModalData}
          setLoading={setLoading}
          handleFunction={handleFunction}
        />
      </div>
    </>
  );
}
